import {Dialog, Button, Loading, Icon, Li, TransitionDiv} from '@startlibs/components'
import { formatDate, getColor, getFetcher, postFetcher } from '@startlibs/utils'
import React, {  useEffect, useRef, useState } from 'react'
import _ from 'lodash/fp'
import styled, { css } from 'styled-components';
import { AutoComplete, Errors, Field, FormValue, ToggleCheckbox, WithForm } from '@startlibs/form';
import { useNotificationRef } from '../../components/Notifications';
import { getDate, getDateValue, getDateYear } from '../../utils/utils';
import { fetchSuggestions, getTagsFromParam } from '../../utils/tagFetch';
import { DATE_FORMAT } from '../../config/DateFormat';
import { AutocompleteBox } from '../../components/AutocompleteBox';
import { getLabel } from './StudyCell';
import { isPastYearToggleDisabled, isUsingStudyPastYear, isUsingUploadedPastYear } from '../../hooks/useIsOrderingBy';
import { transparentize, darken } from 'polished';

const HorizontalSeparator = styled.hr`
  border-top: 1px solid ${getColor('gray210')};
  margin: 1.75rem 0rem 1.5rem;
`

const CardFilters = styled.div`
  border: 1px solid ${getColor('gray210')};
  padding: 1rem;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 0.75rem;
  div + div {
    margin-top: 0.25rem;
  }
`

const InfoMessage = styled.p`
  font-size: 13px;
  color: ${getColor('gray120')};
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`

const ColumnsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -0.25rem;
`

const ColumnItem = styled.span`
  margin: 0.25rem;
  border-radius: 5px;
  position: relative;
  cursor: pointer;
  transition: 0.2s ease;
  font-size: 13px;
  ${props => props.isSelected ? css`
    background: ${props => (transparentize(0.9, getColor('main')(props)))};
    padding: 0.25rem 2rem 0.25rem 0.5rem;
    :hover {
      background: ${props => (transparentize(0.82, getColor('main')(props)))};
    }
    :active {
      background: ${props => (transparentize(0.78, getColor('main')(props)))};
    }
    `:`
    background: white;
    box-shadow: inset 0 0 0 1px rgba(0,0,0,0.15);
    padding: 0.25rem 1.25rem;
    :hover {
      background: rgb(240,240,240);
    }
    :active {
      background: rgb(235,235,235);
    }
  `}
  ${Icon}{
    color: ${getColor('main')};
    font-size: 14px;
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
  }
`

const TagsFieldContainer = styled.div`
  background: ${getColor('gray240')};
  padding: 1.25rem 1rem 1px;
  margin-top: -1.25rem;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  position: relative;
  :after {
    content: '';
    position: absolute;
    top: 3px;
    left: 1rem;
    right: 1rem;
    border-top: 1px solid ${getColor('gray210')};
  }
`

const TagsToggleCheckbox = styled(ToggleCheckbox)`
  position: relative;
  z-index: 2;
  padding-top: 1rem;
  padding-bottom: 1rem;
  &, :focus-within {
    background: ${getColor('gray240')};
  }
  :hover {
    background: ${props => (darken(0.05, getColor('gray240')(props)))};
  }
  :active {
    background: ${props => (darken(0.075, getColor('gray240')(props)))};
  }
`

const ToggleLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
`

const NoTranslateLI = ({...props}) => <Li className={"notranslate"} {...props} />
NoTranslateLI.isKeyboardNavigable = true

const validateTag = (v) => ({errors: true})

export const DataExportDialog = ({params, closeDialog}) => {

  const [isLoading, setIsLoading] = useState(true)
  const [,setNotification] = useNotificationRef()
  
  const [exportCsvToken, setExportCsvToken] = useState()
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const formRef = useRef()

  const dateOfBirth = getDateValue('dateOfBirth',params)
  const studyDate = getDateValue('studyDate',params)
  const studyDateEnd = getDateValue('studyDateEnd',params)
  const uploadDateInit = getDateValue('uploadDateInit',params)
  const uploadDateEnd = getDateValue('uploadDateEnd',params)
  const patientId = params.patientId
  const studyDateSameDay = studyDateEnd && studyDate && studyDate.getTime() === studyDateEnd.getTime()
  const uploadDateSameDay = uploadDateEnd && uploadDateInit && uploadDateEnd.getTime() === uploadDateInit.getTime()

  const [csvExportOptions, setCsvExportOptions] = useState()
  const [columnsData, setColumnsData] = useState([
    {
      label: 'Patient Name',
      selected: true,
    },
    {
      label: 'Patient ID',
      selected: true,
    },
    {
      label: 'Patient DOB',
      selected: true,
    },
    {
      label: 'Study Modality',
      selected: true,
    },
    {
      label: 'Study Description',
      selected: true,
    },
    {
      label: 'Study Date',
      selected: true,
    },
    {
      label: 'Study ID',
      selected: true,
    },
    {
      label: 'Accession Number',
      selected: true,
    },
    {
      label: 'Number of Images',
      selected: true,
    },
    {
      label: 'Ref. Physician',
      selected: true,
    },
    {
      label: 'Institution',
      selected: true,
    },
    {
      label: 'Assignment',
      selected: false,
    },
    {
      label: 'Status',
      selected: false,
    },
    {
      label: 'Upload Date',
      selected: true,
    },
    {
      label: 'Reported by',
      selected: true,
    },
    {
      label: 'Report Status',
      selected: false,
    },
    {
      label: 'Tags',
      selected: true,
    }
  ])
  
  let filtersCount = 0;
  // TODO CHECK URL HERE
  let url = (window.WORKLIST_ORIGIN || "") +`/worklist/api/studies/csv`

  // Increment the counter for each individual filter that is set
  if (dateOfBirth) filtersCount++;
  // if (patientId) filtersCount++;
  if (params.search) filtersCount++;
  if (params.tag) filtersCount++;

  // Increment the counter by one if either or both studyDate and studyDateEnd are set
  if (studyDate || studyDateEnd) filtersCount++;

  // Increment the counter by one if either or both uploadDateInit and uploadDateEnd are set
  if (uploadDateInit || uploadDateEnd) filtersCount++;

  const lastYearDate = formatDate(getDateYear(-1), "yyyy-MM-dd")
  const todayDate = formatDate(getDate(0), "yyyy-MM-dd")

  const isPastYearActive = params.pastYear
  if(isPastYearActive){
    filtersCount++
    if(isPastYearToggleDisabled(params)){
      filtersCount--
    }
  }

  const filters = [];

  if (params.search) {
    filters.push({ label: "Key words", value: params.search });
  }

  if (dateOfBirth) {
    filters.push({ label: "DOB", value: formatDate(dateOfBirth, DATE_FORMAT()) });
  }

  if (!studyDateSameDay && studyDate && studyDateEnd) {
    filters.push({
      label: "Study date between",
      value: `${formatDate(studyDate, DATE_FORMAT())} and ${formatDate(studyDateEnd, DATE_FORMAT())}`
    });
  } else if (studyDateSameDay && studyDate) {
    filters.push({ label: "Study date", value: formatDate(studyDate, DATE_FORMAT()) });
  } else if (!studyDateEnd && studyDate) {
    filters.push({ label: "Study date after", value: formatDate(studyDate, DATE_FORMAT()) });
  } else if (studyDateEnd && !studyDate) {
    filters.push({ label: "Study date before", value: formatDate(studyDateEnd, DATE_FORMAT()) });
  }

  if (!uploadDateSameDay && uploadDateEnd && uploadDateInit) {
    filters.push({
      label: "Uploaded between",
      value: `${formatDate(uploadDateInit, DATE_FORMAT())} and ${formatDate(uploadDateEnd, DATE_FORMAT())}`
    });
  } else if (uploadDateSameDay && uploadDateInit) {
    filters.push({ label: "Uploaded on", value: formatDate(uploadDateInit, DATE_FORMAT()) });
  } else if (!uploadDateEnd && uploadDateInit) {
    filters.push({ label: "Uploaded after", value: formatDate(uploadDateInit, DATE_FORMAT()) });
  } else if (uploadDateEnd && !uploadDateInit) {
    filters.push({ label: "Uploaded before", value: formatDate(uploadDateEnd, DATE_FORMAT()) });
  }

  if (isPastYearActive && isUsingUploadedPastYear(params)) {
    filters.push({ label: "Uploaded between", value: `${lastYearDate} and ${todayDate}` });
  }

  if (isPastYearActive && isUsingStudyPastYear(params)) {
    filters.push({ label: "Study date between", value: `${lastYearDate} and ${todayDate}` });
  }

  if (getTagsFromParam(params.tag).length > 0 ) {
    filters.push({ label: "Tags", value: getTagsFromParam(params.tag).map(tag => tag.description).join(", ") });
  }

  useEffect(() => {
  
    getFetcher('/csvExportOptionsGet.php')
      .then((data) => {
        // First, ensure data is a string to safely call string methods
        const dataStr = typeof data === 'string' ? data : JSON.stringify(data);

        // Check if the original string data is primarily whitespace (or empty)
        if (!dataStr.trim() || dataStr.trim().length === 0) {
          setCsvExportOptions({
            columnsToExport: columnsData,
            exportTagsAsColumns: false,
            tagsAsColumns: []
          });
        } else {
          try {
            // Attempt to parse data as JSON if it's not already an object
            const jsonData = typeof data === 'object' ? data : JSON.parse(dataStr);

            if (jsonData && typeof jsonData === 'object' && Object.keys(jsonData).length > 0) {
              // Check if there is an item with label  "Report Status"
              if (!jsonData.columnsToExport.find(item => item.label == 'Report Status')) {
                // jsonData.columnsToExport.push({label: 'Report Status', selected: false})
                jsonData.columnsToExport.splice(jsonData.columnsToExport.length-1, 0, {label: 'Report Status', selected: false});
              }
              setCsvExportOptions(jsonData);
            } else {
              // Handle non-object JSON or empty objects
              setCsvExportOptions({
                columnsToExport: columnsData,
                exportTagsAsColumns: false,
                tagsAsColumns: []
              });
            }
          } catch (e) {
            // Default handling for non-JSON or problematic cases
            setCsvExportOptions({
              columnsToExport: columnsData,
              exportTagsAsColumns: false,
              tagsAsColumns: []
            });
          }
        }
      })
      .catch((err) => {
        console.log(err)
        setCsvExportOptions({
          columnsToExport: columnsData,
          exportTagsAsColumns: false,
          tagsAsColumns: []
        })
      })
      .finally(
        () => {
          setIsLoading(false)
        }
      )

    getFetcher(`/servicetokenlogged.php?service=worklistStudies`)
      .then((response) => {
        setExportCsvToken(response.trim())
      })
      .catch((error) => {
        console.log(error)
      })
  }, [])

  
  if(isLoading){
    return <Dialog
      title="Data Export"
      closeDialog={closeDialog}
      css={'max-width: 650px;'}
      footer={() => {
        return <>
          <Button onClick={() => {
            closeDialog();
          }}>Cancel</Button>
          <Button highlight
            type="submit"
            disabled={true}
          >
            Export to CSV
          </Button>
        </>
    }}>
      <div
        style={{
          height: '280px',
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Loading size={40} borderWidth={6}/>
      </div>
    </Dialog>
  }
  

  return <>
    <WithForm
      ref={formRef}
      action={(values) => {}}
      values={csvExportOptions}
    >{form =>
        <Dialog
          title="Export to CSV"
          closeDialog={closeDialog}
          css={'max-width: 650px;'}
          footer={() => {
            return <>
              <Button onClick={() => {
                closeDialog();
              }}>Cancel</Button>
              <Button highlight
                type="submit"
                isLoading={isLoadingCsv}
                onClick={() => {
                  setIsLoadingCsv(true)
                  try{
                    const formValues = form.getValues()
                    if(formValues.exportTagsAsColumns && formValues.tagsAsColumns.length > 5){
                      form.setError('tagsAsColumns', 'Please select up to 5 tags to be exported as columns')
                      setIsLoadingCsv(false)
                      return
                    }else{
                      form.removeErrorPath('tagsAsColumns')
                    }
                    var fileName = '';
                    const updatedCsvExportOptions = {
                      ...csvExportOptions,
                      exportTagsAsColumns: formValues.exportTagsAsColumns,
                      tagsAsColumns: formValues.exportTagsAsColumns ? formValues.tagsAsColumns : []
                    }
                    const columns = csvExportOptions.columnsToExport.filter(col => col.selected).map(col => col.label).join(', ')
                    const tagsAsColumns = formValues.tagsAsColumns.map(tag => tag.description).join(', ')

                    const csvFilters = {
                      studyDate: params.studyDate 
                        ? params.studyDate 
                        : (isPastYearActive && isUsingStudyPastYear(params)) ? lastYearDate : null,
                      studyDateEnd: params.studyDateEnd
                        ? params.studyDateEnd
                        : (isPastYearActive && isUsingStudyPastYear(params)) ? todayDate : null,
                      uploadDate: params.uploadDateInit 
                        ? params.uploadDateInit 
                        : (isPastYearActive && isUsingUploadedPastYear(params)) ? lastYearDate : null,
                      uploadDateEnd: params.uploadDateEnd
                        ? params.uploadDateEnd
                        : (isPastYearActive && isUsingUploadedPastYear(params)) ? todayDate : null,
                      tags: params.tag ? params.tag.split(',').map(tag => tag.trim()) : [],
                      keyword: params.search,
                      dob: params.dateOfBirth && params.dateOfBirth !== '' ? params.dateOfBirth : null,
                      studyStatus: params.studyStatus
                    }
                    // remove null values
                    const newCsvFilters = Object.fromEntries(
                      Object.entries(csvFilters).filter(([key, value]) => value !== null)
                    );

                    // TODO ONLY SELECTED
                    const requestBody = JSON.stringify({
                      // ...csvExportOptions,
                      columnsToExport: csvExportOptions.columnsToExport.filter(col => col.selected),
                      exportTagsAsColumns: formValues.exportTagsAsColumns,
                      tagsAsColumns: formValues.exportTagsAsColumns ? formValues.tagsAsColumns : [],
                      filters: newCsvFilters
                    });

                    // Update default options
                    postFetcher('/csvExportOptionsUpdate.php', {csvOptions: updatedCsvExportOptions})
                      .then((data) => {})
                      .catch((err) => {console.log(err)})
                     
                    // Save logs
                    postFetcher('/csvExportLog.php', {
                      tags: tagsAsColumns,
                      columns: columns,
                      filters: filters
                    })
                      .then((data) => {})
                      .catch((err) => {console.log(err)})

                    // Fetch to generate and download CSV
                    fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${exportCsvToken}`
                        },
                        body: requestBody
                    })
                    .then(response => {
                        // Check if request was successful
                        if (!response.ok) {
                            throw new Error(`HTTP error! Status: ${response.status}`);
                        }
                        const headerValue = response.headers.get('Content-Disposition');
                        fileName = 'data.csv';
                        setIsLoadingCsv(false);
                        return response.text();  // or response.text() if the response is plain text
                    })
                    .then(data => {
                        const blob = new Blob([data], { type: 'text/csv' });
                
                        // Create an anchor element and set its href to the blob URL
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = fileName; // Set the file name for the download
                        a.style.display = 'none';
                    
                        document.body.appendChild(a); // Temporarily add the anchor element to the document
                        a.click(); // Initiate the download
                    
                        document.body.removeChild(a); // Remove the anchor element from the document
                        URL.revokeObjectURL(a.href); // Release the allocated memor

                        closeDialog()
                    })
                    .catch(error => {
                        setIsLoadingCsv(false);
                        console.error('There was a problem with the fetch operation:', error);
                        setNotification({type:"alert",msg:<>It was not possible to export data. Please try again later.</>})
                    });
                  }catch(err){
                    console.log(err)
                    setIsLoadingCsv(false)
                  }
                }}
              >
                Export to CSV
              </Button>
            </>
          }}
        >
          {isLoading 
            ?
              <Loading size={40} borderWidth={6}/>
            :
              <>
              
                <Field
                  label={filtersCount === 1 ? "1 filter applied" : ((filtersCount > 0 ? filtersCount : 'No') + " filters applied")}
                  margin="big"
                  css="margin-top:1rem;"
                >
                {filtersCount > 0 && <CardFilters>
                  {params.search && <div><b>Key words: </b>{params.search}</div>}
                  {
                    !!dateOfBirth && <div><b>DOB:</b> {formatDate(dateOfBirth,DATE_FORMAT())}</div>
                  }
                  {
                    !studyDateSameDay  && !!studyDate && !!studyDateEnd && <div><b>Study date between:</b> {formatDate(studyDate,DATE_FORMAT())} and {formatDate(studyDateEnd,DATE_FORMAT())}</div>
                  }
                  {
                    !!studyDateSameDay && <div><b>Study date:</b> {formatDate(studyDate,DATE_FORMAT())}</div>
                  }
                  {
                    !studyDateEnd && !!studyDate && <div><b>Study date after:</b> {formatDate(studyDate,DATE_FORMAT())}</div>
                  }
                  {
                    !!studyDateEnd && !studyDate && <div><b>Study date before:</b> {formatDate(studyDateEnd,DATE_FORMAT())}</div>
                  }
                  {
                    !uploadDateSameDay && !!uploadDateEnd && !!uploadDateInit && <div><b>Uploaded between:</b> {formatDate(uploadDateInit,DATE_FORMAT())} and {formatDate(uploadDateEnd,DATE_FORMAT())}</div>
                  }
                  {
                    !!uploadDateSameDay && <div><b>Uploaded on:</b> {formatDate(uploadDateInit,DATE_FORMAT())}</div>
                  }
                  {
                    !uploadDateEnd && !!uploadDateInit && <div><b>Uploaded after:</b> {formatDate(uploadDateInit,DATE_FORMAT())}</div>
                  }
                  {
                    !!uploadDateEnd && !uploadDateInit &&
                    <div><b>Uploaded before:</b> {formatDate(uploadDateEnd,DATE_FORMAT())}</div>
                  }
                  {
                    isPastYearActive && isUsingUploadedPastYear(params) &&  <div><b>Uploaded between:</b> {lastYearDate} and {todayDate}</div>
                  }
                  {
                    isPastYearActive && isUsingStudyPastYear(params) && <div><b>Study date between:</b> {lastYearDate} and {todayDate}</div>
                  }
                  {/* {
                    !!patientId && <div><b>Patient ID:</b> {patientId}</div>
                  } */}
                  {
                    getTagsFromParam(params.tag).length > 0 && <div><b>Tags:</b> {getTagsFromParam(params.tag).map(tag => tag.description).join(", ")}</div>
                  }
                  
                </CardFilters>}
                <InfoMessage css="margin-top:-0.25rem;">The generated table is limited to a maximum of 10,000 rows.</InfoMessage>
                </Field>
                <Field 
                  label="Select the columns to export"
                  helpText={"(" + csvExportOptions.columnsToExport.filter(c => c.selected).length + "/" + csvExportOptions.columnsToExport.length + " selected)"}
                  margin="big"
                >
                  <ColumnsContainer>
                    {csvExportOptions.columnsToExport.map(column => 
                      <ColumnItem onClick={() => {
                        setCsvExportOptions({ ...csvExportOptions, columnsToExport: csvExportOptions.columnsToExport.map(c => ({...c, selected: column.label === c.label ? !c.selected : c.selected})) })
                      }} isSelected={column.selected}>{column.label} {column.selected && <Icon icon="check" />}</ColumnItem>
                    )}
                  </ColumnsContainer>
                </Field>
                <Field label="Tags">
                <TagsToggleCheckbox
                  path="exportTagsAsColumns"
                  label={<ToggleLabel>Export tags as separate columns</ToggleLabel>}
                />
                    <FormValue path="exportTagsAsColumns">{selected =>
                    <TransitionDiv>{selected && <TagsFieldContainer>
                      <AutoComplete
                        AutoCompleteLi={NoTranslateLI}
                        CompletedBox={AutocompleteBox}
                        label={'Select up to 5 tags to be exported as separate columns'}
                        minQueryLength={1}
                        confirmIfValueKeys={['Tab']}
                        path="tagsAsColumns"
                        value={[]}
                        autoSelectEquals
                        fetchSuggestions={fetchSuggestions(false)}
                        validation={validateTag}
                        getLabel={getLabel}
                        withDropdown
                        bellowDescText="Use comma, enter or tab to add multiple tags."
                        placeholder="Search for existing tags"
                      />
                    </TagsFieldContainer>
                    }</TransitionDiv>
                  }
                  </FormValue>
                </Field>
                <Errors />
                <HorizontalSeparator />
                <InfoMessage>Please be aware that the generated file contains Protected Health Information. It is the user's responsability to use and store this data appropriately.</InfoMessage>
              </>
          }
        </Dialog>
    }</WithForm>
    
  </>

}